import { FC, useMemo, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  Stack,
  Upload,
  useToaster,
} from '@forto-dev/phoenix-ui-core/common'

import { useAuth } from '@/features/auth'
import { useLocalisation } from '@/utils/localisation'
import { specialCharacterRegex } from '@/utils/validation'

import { buildDocumentTypeLabelMap } from '../../../utils/localisation/documentTypes'
import { documentTypesQueryOptions } from '../api/documents.service'
import { createCreateDocumentSchema, CreateDocumentFormSchemaType } from '../api/schema'
import { useEntityDocumentCreate } from '../api/useEntityDocumentCreate'
import { entityDocumentsQueryOptions } from '../api/useEntityDocuments'
import { useEntityDocumentUpdate } from '../api/useEntityDocumentUpdate'
import { Document } from '../domain'

type OnUploadArgs = {
  data: CreateDocumentFormSchemaType
  uploadFile?: File
  documentWithSameRole?: Document
  onUploadComplete: () => void
}
type LeanDocumentUploadProps = {
  isUploading?: boolean
  documents: Document[]
  onUpload: (args: OnUploadArgs) => void

  // Implementing this for backwards compatibility, ideally this logic should be handled by CreateDocumentFormSchema
  isRoleRequired?: boolean
}

export const LeanDocumentUpload: FC<LeanDocumentUploadProps> = ({
  onUpload,
  isUploading = false,
  documents,
  isRoleRequired,
}) => {
  const [uploadFormKey, setUploadFormKey] = useState(Math.random())
  const [uploadFile, setUploadFile] = useState<File | undefined>(undefined)
  const { data: documentTypes } = useSuspenseQuery(documentTypesQueryOptions())
  const { isCustomerActive } = useAuth()
  const { translate } = useLocalisation()
  const toaster = useToaster()

  const createDocumentSchema = createCreateDocumentSchema(isRoleRequired)

  const {
    getValues,
    setValue,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    resolver: zodResolver(createDocumentSchema),
    defaultValues: {
      role: '',
      replace: false,
    },
  })

  const roles = useMemo(() => buildDocumentTypeLabelMap(translate, documentTypes), [translate, documentTypes])

  if (!isCustomerActive) {
    return null
  }

  const documentWithSameRole = documents.find((document) => document.documentTypeCode === getValues('role'))

  const handleUplaodComplete = () => setUploadFile(undefined)

  const onSubmit: SubmitHandler<CreateDocumentFormSchemaType> = async (data) => {
    onUpload({ data, uploadFile, documentWithSameRole, onUploadComplete: handleUplaodComplete })
  }

  return (
    <>
      <Upload
        key={uploadFormKey}
        label={translate('documents.upload.label')}
        description={translate('documents.upload.description')}
        accept="image/jpeg,image/png,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={(files) => {
          const selectedFile = files?.[0]
          if (selectedFile) {
            // Check for special characters in the file name
            if (specialCharacterRegex.test(selectedFile.name)) {
              toaster({
                title: translate('documents.upload.error.label'),
                description: translate('documents.upload.error.description'),
                variant: 'negative',
              })
              setUploadFile(undefined)
              setUploadFormKey(Math.random())
              return
            }
          }
          setUploadFile(selectedFile ?? undefined)
          // Enforce re-render of the input field to clear any existing selection
          setUploadFormKey(Math.random())
        }}
      />

      <Dialog
        open={Boolean(uploadFile)}
        onOpenChange={(isOpen: boolean) => {
          if (!isOpen) {
            setUploadFile(undefined)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {translate('documents.upload.dialog.title', {
                name: uploadFile?.name,
              })}
            </DialogTitle>
          </DialogHeader>
          <Stack gap={16}>
            <Select
              label={translate('documents.upload.dialog.fields.type.label')}
              value={getValues('role')}
              onChange={(newRole) => {
                setValue('role', newRole, { shouldValidate: true })
              }}
              state={errors.role ? 'negative' : undefined}
              hint={errors.role ? errors.role.message : undefined}
              items={Object.entries(roles).map(([role, label]) => ({
                value: role,
                label: label,
              }))}
              searchable
            />

            {/*
            // This checkbox when ticked is supposed to replace the existing document
            // But the current implementation adds a new file with the same upload date of the previous one
            // Until established that it's needed (Old SHIP didn't have it) and the behavior fixed, it should not show
            {documentWithSameRole && !documentWithSameRole.isMultipleInstancesAllowed ? (
              <Checkbox
                id={`${formId}-replace`}
                label={translate('documents.upload.dialog.fields.replace.label')}
                checked={getValues('replace')}
                onCheckedChange={(checked) => {
                  setValue('replace', Boolean(checked), { shouldValidate: true })
                }}
              />
            ) : null}
             */}
          </Stack>
          <DialogFooter>
            <Button onClick={handleSubmit(onSubmit)} disabled={isUploading || (isRoleRequired && !isValid)}>
              {translate('documents.upload.dialog.actions.upload')}
            </Button>
            <Button variant="outline-primary" onClick={() => setUploadFile(undefined)}>
              {translate('documents.upload.dialog.actions.cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export type DocumentUploadProps = {
  id: string
  onComplete?: () => void

  isRoleRequired?: boolean
}
export const DocumentUpload: FC<DocumentUploadProps> = ({ id, onComplete, isRoleRequired }) => {
  const { translate } = useLocalisation()
  const { customer } = useAuth()
  const toaster = useToaster()
  const queryClient = useQueryClient()
  const createDocumentMutation = useEntityDocumentCreate()
  const uploadDocumentMutation = useEntityDocumentUpdate()
  const [isUploading, setIsUploading] = useState(false)

  const documents = useSuspenseQuery(entityDocumentsQueryOptions(id))

  const onSubmit = async ({ data, uploadFile, documentWithSameRole, onUploadComplete }: OnUploadArgs) => {
    if (uploadFile && !isUploading) {
      // Check for special characters in the file name
      if (specialCharacterRegex.test(uploadFile.name)) {
        toaster({
          title: translate('documents.upload.error.label'),
          description: translate('documents.upload.error.description'),
          variant: 'negative',
        })
        return // Stop the upload process
      }

      setIsUploading(true)
      try {
        if (data.replace && documentWithSameRole) {
          await uploadDocumentMutation.mutateAsync({
            documentId: documentWithSameRole.id,
            entityId: id,
            data: {
              file: uploadFile,
              state: 'published',
              mimeType: uploadFile.type,
              customerID: customer?._id ?? '',
              source: 'customer',
              documentTypeCode: data.role,
            },
          })

          await queryClient.invalidateQueries({
            queryKey: ['entityDocuments', id],
          })

          toaster({
            title: translate('documents.upload.notifications.updated'),
          })
        } else {
          await createDocumentMutation.mutateAsync({
            entityId: id,
            data: {
              file: uploadFile,
              state: 'published',
              mimeType: uploadFile.type,
              customerID: customer?._id ?? '',
              source: 'customer',
              documentTypeCode: data.role,
            },
          })

          await queryClient.invalidateQueries({
            queryKey: ['entityDocuments', id],
          })

          toaster({
            title: translate('documents.upload.notifications.uploaded'),
          })
        }
      } finally {
        setIsUploading(false)
        onUploadComplete()
        onComplete?.()
      }
    }
  }

  return (
    <LeanDocumentUpload
      isUploading={isUploading}
      onUpload={onSubmit}
      documents={documents.data.results}
      isRoleRequired={isRoleRequired}
    />
  )
}
